// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { DisclosedContract as TokenStandardApiDisclosedContract } from '@digitalasset/allocation-instruction-api-ts-client';
import { DisclosedContract as ApiDisclosedContract } from '@digitalasset/utility-operator-api-ts-client';
import { GridFilterItem } from '@mui/x-data-grid';
import { Tuple2 } from '@daml.js/daml-prim-types/lib/DA/Types';
import { Set } from '@daml.js/daml-set-types/lib/DA/Set/Types';
import { InstrumentIdentifier } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Types';
import { ContractId, DisclosedContract, emptyMap } from '@daml/types';

export const fmt = (x: number | string, decimals?: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: 20,
  });
  const res = formatter.format(typeof x === 'string' ? parseFloat(x) : x);
  return res;
};

export const prettyParty = (party: string): string => {
  const [p] = party.split('::');
  return p;
};
export const instrumentEquals = (k1: InstrumentIdentifier, k2: InstrumentIdentifier): boolean => {
  return k1.source === k2.source && k1.id === k2.id && k1.scheme === k2.scheme;
};

export const createSet = <T>(vals: T[]): Set<T> => {
  let map = emptyMap<T, object>();
  for (let i = 0; i < vals.length; i += 1) map = map.set(vals[i], {});
  return { map };
};

export function values<T>(set: Set<T>): T[] {
  try {
    return set.map.entriesArray().map((v) => v[0]);
  } catch {
    console.error(`Could not get values from set: ${JSON.stringify(set)}`);
    return [];
  }
}
export const shorten = (s?: string, len?: number): string => {
  if (!s) {
    return '';
  }
  return s.substring(0, len || 8);
};
export const extractPrefix = (input: string, delimiter: string = '::'): string => {
  const [prefix] = input.split(delimiter);
  return prefix;
};

export const filterToSqlText = (f: GridFilterItem): string => {
  if (typeof f.value === 'string') {
    const s = f.value.replaceAll('%', '');
    if (f.operator === 'startsWith') {
      return `${s}%`;
    }
    if (f.operator === 'equals') {
      return s;
    }
  }
  throw new Error('Invalid filter');
};

export const filterDateToSqlText = (f: GridFilterItem): string => {
  const date = new Date(f.value as string);
  if (f.operator === 'before') {
    return `< '${date.toISOString()}'`;
  }
  if (f.operator === 'after') {
    return `> '${date.toISOString()}'`;
  }
  throw new Error('Invalid filter');
};

export const parseDateString = (dateAsString: string): Date => {
  const parsedFromString = Date.parse(dateAsString);
  return new Date(parsedFromString);
};

const parseDateStringWithTimezone = (dateAsString: string): Date => {
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateAsString);
  const normalized = hasTimezone ? dateAsString : `${dateAsString}Z`;
  const parsedFromString = Date.parse(normalized);
  return new Date(parsedFromString);
};

export const parseTimestampToDate = (timestamp: string | Date | null | undefined): Date | null => {
  if (!timestamp) return null;
  const date = typeof timestamp === 'string' ? parseDateStringWithTimezone(timestamp) : timestamp;
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

/**
 * Format a timestamp string or Date object to a UNIX date-like format.
 * Returns date and time in the format: "Mon 03 Feb 2025 02:30:45 PM UTC"
 */
export const formatTimestamp = (timestamp: string | Date | null | undefined): string => {
  const date = parseTimestampToDate(timestamp);
  if (!date) return '';

  const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' });
  const day = date.toLocaleString('en-US', { day: '2-digit', timeZone: 'UTC' });
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  const time = date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });

  return `${dayOfWeek} ${day} ${month} ${year} ${time} UTC`;
};

export const toDamlDisclosedContract = (
  disclosedContract: ApiDisclosedContract,
): DisclosedContract => ({
  contractId: disclosedContract.contractId as ContractId<unknown>,
  templateId: disclosedContract.templateId,
  createdEventBlob: disclosedContract.createdEventBlob,
});

export const toTokenStandardDisclosedContract = (
  disclosedContract: TokenStandardApiDisclosedContract,
): DisclosedContract => ({
  contractId: disclosedContract.contractId as ContractId<unknown>,
  templateId: disclosedContract.templateId,
  createdEventBlob: disclosedContract.createdEventBlob,
});

interface Show {
  toString(): string;
}

/**
 * Converts a Daml Tuple2 to a string representation in the format "(elem1, elem2)".
 * @param tuple - The Tuple2 containing two elements that implement toString().
 * @returns A string representation of the tuple.
 */
export const tupleToString = <T1 extends Show, T2 extends Show>(tuple: Tuple2<T1, T2>): string => {
  const elem1String = tuple._1.toString();
  const elem2String = tuple._2.toString();
  return `(${elem1String}, ${elem2String})`;
};
